import { useState } from 'react';
import { useFieldContext } from '@bridebook/aura';
import { Calendar } from '../../calendar/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover/popover';
import { FakeInput } from '../fake-input';
import type { DateInputProps } from './types';
import { formatDisplayDate, parseDateString, toDateString } from './utils';

/** TLDR: FakeInput + Calendar popover — used on desktop (hover-capable) devices. */
export const DateInputDesktop = ({
  id,
  value,
  onChange,
  onBlur,
  disabled: disabledProp,
  error: errorProp,
  placeholder,
  icon,
  className,
  classNames,
  modal,
  min,
  max,
  locale,
  formatDate,
  dayPickerProps,
  showClearBtn = true,
  ...fakeInputProps
}: DateInputProps) => {
  const context = useFieldContext();
  const disabled = disabledProp ?? context?.disabled ?? false;
  const error = errorProp ?? context?.error ?? false;

  const [open, setOpen] = useState(false);
  const [disableCloseAnimationOnNextClose, setDisableCloseAnimationOnNextClose] = useState(false);
  const localeCode =
    locale && 'code' in locale && typeof locale.code === 'string' ? locale.code : undefined;

  const displayValue = formatDate ? formatDate(value) : formatDisplayDate(value, localeCode);
  const selectedDate = parseDateString(value);
  const fromDate = parseDateString(min ?? '');
  const toDate = parseDateString(max ?? '');

  const currentYear = new Date().getFullYear();
  const startMonth = fromDate ?? new Date(currentYear - 5, 0);
  const endMonth = toDate ?? new Date(currentYear + 10, 11);

  // react-day-picker v9 disables days via `disabled` prop
  const disabledDays = [
    ...(fromDate ? [{ before: fromDate }] : []),
    ...(toDate ? [{ after: toDate }] : []),
  ];

  const handleSelect = (date: Date | undefined) => {
    if (!date) return;
    setDisableCloseAnimationOnNextClose(true);
    onChange(toDateString(date));
    setOpen((previousOpen) => {
      if (previousOpen) {
        onBlur?.();
      }
      return false;
    });
  };

  const handleClear = () => {
    onChange('');
    onBlur?.();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      // Reset one-shot close behavior once the popover is opened again.
      setDisableCloseAnimationOnNextClose(false);
    }

    setOpen((previousOpen) => {
      if (previousOpen && !nextOpen) {
        onBlur?.();
      }
      return nextOpen;
    });
  };

  return (
    <Popover closeOnScroll modal={modal} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <FakeInput
          id={id}
          placeholder={placeholder}
          icon={icon}
          value={displayValue}
          error={error}
          disabled={disabled}
          focused={open}
          showClearBtn={showClearBtn}
          onClear={handleClear}
          className={className}
          classNames={classNames}
          {...fakeInputProps}
        />
      </PopoverTrigger>

      <PopoverContent
        sideOffset={8}
        className="rounded-2xl bg-white shadow-bb-shadow"
        disabledCloseAnimation={disableCloseAnimationOnNextClose}>
        <Calendar
          locale={locale}
          mode="single"
          captionLayout="dropdown"
          selected={selectedDate}
          onSelect={handleSelect}
          defaultMonth={selectedDate}
          startMonth={startMonth}
          endMonth={endMonth}
          disabled={disabledDays}
          {...dayPickerProps}
        />
      </PopoverContent>
    </Popover>
  );
};
